// ============================================================================
// src/lib/sheepBatchOperationApi.ts — the one client of public.sheep_batch_operation
// ----------------------------------------------------------------------------
// Migration 241 (R4 sheep) replaces the sheep processing batch page's direct
// PostgREST writes with ONE audited server entrypoint. It applies a narrow
// change to one batch under its row lock (the lock attach, detach and delete
// take), after checking the caller's expected-before values, and writes the
// sheep.processing Activity row and an immutable request receipt in the same
// transaction. The shape follows src/lib/pigOperationsApi.ts (231/235).
//
// WHAT THIS MODULE OWNS: the transport and the meaning of every reply.
//
//   * REQUEST IDS. One per user action (`newSheepRequestId`). A retry of the
//     same action reuses it: the server replays the stored result or applies
//     the operation once.
//   * RETRIES. A transport failure (no reply, timeout) is retried here with the
//     SAME id, which the receipt makes safe. A database answer (any SQLSTATE)
//     is never retried.
//   * EXPECTED VALUES. The caller sends the value it READ (the persisted row or
//     the persisted grid cell), never a form value; the server compares JSON.
//
// The catalog entries in routeMutationCatalog throw on anything but a saved
// or unchanged outcome, so the page's existing catch blocks show the notice.
// ============================================================================

import type {SupabaseClient} from './supabaseTypes.js';
import {newClientSubmissionId} from './clientSubmissionId.js';

export type SheepBatchOperation = 'batch.create' | 'batch.update' | 'batch.status' | 'batch.weight';

type Json = unknown;

export type SheepBatchOpRequest = {
  requestId: string;
  operation: SheepBatchOperation;
  target: {batch_id: string; sheep_id?: string};
  expected?: Record<string, Json>;
  change: Record<string, Json>;
};

export type SheepConflict = {field: string; expected: Json; current: Json; sheep_id?: string};

export type SheepBatchOpOutcome =
  | {status: 'saved'; kind: 'created' | 'updated'; written: Record<string, Json>; eventIds: string[]; replayed: boolean}
  | {status: 'no_change'}
  | {status: 'conflict'; conflicts: SheepConflict[]}
  | {status: 'refused'; reason: string}
  | {status: 'rejected'; code: string; message: string}
  | {status: 'unknown'; message: string};

/** A fresh id for ONE user action. Keep it with the action and reuse it on retry. */
export function newSheepRequestId(): string {
  return 'sheep-' + newClientSubmissionId();
}

const ATTEMPTS = 3;
const ATTEMPT_TIMEOUT_MS = 8000;
// A five-character SQLSTATE (22023, 23505, 42501, WCF01, ...) is the database's answer.
const SQLSTATE = /^[0-9A-Z]{5}$/;

type RpcError = {code?: string; message?: string} | null;

/** Read one reply. Anything that does not answer THIS request is not a receipt. */
export function interpretSheepReply(request: SheepBatchOpRequest, data: unknown): SheepBatchOpOutcome {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, Json>;
  if (d.request_id !== request.requestId || d.operation !== request.operation) {
    return {status: 'unknown', message: 'Could not confirm this change was saved. Reload to check.'};
  }
  switch (d.kind) {
    case 'created':
    case 'updated':
      if (d.ok !== true) break;
      return {
        status: 'saved',
        kind: d.kind,
        written: (d.written && typeof d.written === 'object' ? d.written : {}) as Record<string, Json>,
        eventIds: Array.isArray(d.event_ids) ? (d.event_ids as string[]) : [],
        replayed: d.replayed === true,
      };
    case 'no_change':
      return {status: 'no_change'};
    case 'conflict':
      return {status: 'conflict', conflicts: Array.isArray(d.conflicts) ? (d.conflicts as SheepConflict[]) : []};
    case 'refused':
      return {status: 'refused', reason: typeof d.reason === 'string' ? d.reason : 'refused'};
  }
  return {status: 'unknown', message: 'Could not confirm this change was saved. Reload to check.'};
}

/**
 * Apply one sheep batch operation. Never throws. Transport failures are retried
 * with the same request id; a database answer comes back at once.
 */
export async function applySheepBatchOperation(
  sb: SupabaseClient,
  request: SheepBatchOpRequest,
): Promise<SheepBatchOpOutcome> {
  let lastMessage = 'No reply from the server.';
  for (let attempt = 1; attempt <= ATTEMPTS; attempt++) {
    let timer: ReturnType<typeof setTimeout> | null = null;
    try {
      const call = sb.rpc('sheep_batch_operation', {
        p_request_id: request.requestId,
        p_operation: request.operation,
        p_target: request.target,
        p_expected: request.expected ?? {},
        p_change: request.change,
      });
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), ATTEMPT_TIMEOUT_MS);
      });
      const {data, error} = (await Promise.race([call, timeout])) as {data: unknown; error: RpcError};
      if (error) {
        if (error.code && SQLSTATE.test(error.code)) {
          return {status: 'rejected', code: error.code, message: error.message || 'The change was refused.'};
        }
        throw new Error(error.message || 'transport');
      }
      return interpretSheepReply(request, data);
    } catch (e) {
      lastMessage = (e as {message?: string}).message || lastMessage;
      if (attempt < ATTEMPTS) await new Promise((r) => setTimeout(r, 1000 * attempt));
    } finally {
      if (timer !== null) clearTimeout(timer);
    }
  }
  console.warn('sheep_batch_operation: no confirmed reply after retries:', lastMessage);
  return {status: 'unknown', message: 'Could not reach the server. Your change is kept; retry.'};
}

/** The sentence for an outcome that did not save, in the page's own terms. */
export function sheepOutcomeMessage(
  outcome: Exclude<SheepBatchOpOutcome, {status: 'saved'} | {status: 'no_change'}>,
): string {
  switch (outcome.status) {
    case 'conflict':
      return 'Changed by someone else since this page loaded. Reload to see the current values.';
    case 'refused':
      if (outcome.reason === 'not_attached') return 'This sheep is no longer in this batch. Reload the batch.';
      if (outcome.reason === 'complete_keeps_actual_date')
        return 'A complete batch keeps its actual process date. Change the status first to clear it.';
      if (outcome.reason === 'not_found') return 'This batch was removed elsewhere. Reload to see the current list.';
      return 'The change was refused (' + outcome.reason + ').';
    case 'rejected':
      switch (outcome.code) {
        case '22023':
          return outcome.message.replace(/^sheep_batch_operation: /, '');
        case '23505':
          return outcome.message;
        case '42501':
          return 'Your session or role changed. Sign in again; your change is kept.';
        case 'WCF01':
          return 'This change no longer matches what was saved. Reload and try again.';
        default:
          return outcome.message || 'The change was refused.';
      }
    case 'unknown':
      return outcome.message;
  }
}

/** An operation that did not save, as the error the catalog's callers catch. */
export class SheepBatchOperationError extends Error {
  readonly outcome: Exclude<SheepBatchOpOutcome, {status: 'saved'} | {status: 'no_change'}>;
  readonly code?: string;
  constructor(outcome: Exclude<SheepBatchOpOutcome, {status: 'saved'} | {status: 'no_change'}>) {
    super(sheepOutcomeMessage(outcome));
    this.name = 'SheepBatchOperationError';
    this.outcome = outcome;
    // The page's create path re-allocates a suggested name on a UNIQUE violation.
    if (outcome.status === 'rejected') this.code = outcome.code;
  }
}

/** Run one operation; resolve on a saved or unchanged outcome, throw otherwise. */
export async function runSheepBatchOperation(
  sb: SupabaseClient,
  request: SheepBatchOpRequest,
): Promise<Extract<SheepBatchOpOutcome, {status: 'saved'} | {status: 'no_change'}>> {
  const outcome = await applySheepBatchOperation(sb, request);
  if (outcome.status === 'saved' || outcome.status === 'no_change') return outcome;
  throw new SheepBatchOperationError(outcome);
}
